import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select"
import { CodePill } from "@/accounting/code_pill"
import { DeductionListJson, bulk_parse_backup } from "@/api/deduction"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { Table } from "@tanstack/react-table"
import {
  CircleCheck,
  File,
  Menu,
  Store,
  Tag,
  X,
  Code,
  Wand2,
  LoaderCircle,
  FileSearch,
  Download,
} from "lucide-react"
import { CATEGORY_FORM_SCHEMA, CategoryForm } from "@/deduction_table/CategoryForm"
import { STATUS_ICONS } from "@/deduction_table/columns.tsx"
import { STATUS_VALUES } from "@/deduction_table/status_select.tsx"
import { z } from "zod"
import { api_fetch, User } from "@/api"
import { UserState } from "@/auth/user.tsx"
import { use_coding_wiz_preferences } from "@/api/coding_wiz_preferences"
import { AssignmentForm } from "@/deduction_table/assignment_form"
import { useState } from "preact/hooks"
import { isSuperCoffeeUser, useAsyncEffect } from "@/utils"
import { DEDUCTION_STATUS_LABELS } from "@/utils/deduction_status_labels"
import { build_request_backup_form, bulk_submit_dispute } from "@/api/dispute"
import { bulk_backup_request_limit_error } from "@/deduction_next_action/request_backup"
import { assign_tasks, bulk_complete_tasks, TaskStatus, TaskSubmission } from "@/api/task"
import { PendingTaskConfirmDialog } from "@/deduction_table/pending_task_confirm_dialog.tsx"
import { LedgerAccounts } from "@/accounting/accounts"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { toastResult } from "@/toast.tsx"
import { FullCodingDialog } from "@/accounting/FullCodingDialog.tsx"
import { Split } from "@/api/accounting.tsx"
import { BulkPromotionMatchDialog } from "@/transactions/BulkPromotionMatchDialog.tsx"
import { buildPromotionSearchSeedFromSelection } from "@/accounting_detail/PromotionSelector.tsx"
import {
  BulkCodingRow,
  bulk_code_to_account,
  bulk_full_code,
  start_bulk_coding_wiz_for_rows,
} from "@/accounting/bulk_coding_actions"
import { Star, Sparkles } from "lucide-react"
import { addTriageInProgress, removeTriageInProgress } from "@/deduction_table/triage_state"
import { run_big_wiz } from "@/deduction/run_big_wiz"
import { toast } from "@/components/ui/use-toast.ts"
import { openBulkWizard } from "@/deduction/bulk_wizard_state"
import { CustomWizDeduction, CustomWizDialog } from "@/deduction/CustomWizDialog.tsx"
import { FieldType, OrgFieldConfig } from "@/api/org_field_config.tsx"
import { OrgDropdownCell } from "@/components/org_dropdown.tsx"
import { interaction_target_is_inside_radix_portal } from "@/components/radix_dismiss_guard"
import { Label } from "@/components/ui/label"
import { download_post_export } from "@/utils/download"

interface DeductionTableSelectionBarProps {
  table: Table<DeductionListJson>
  isAccounting: boolean
  orgFieldConfigs?: OrgFieldConfig[]
}

interface BulkCategoryUpdateResponse {
  message: string
}

interface BulkTriageResult {
  deduction_id: string
  success: boolean
  error?: string
}

interface BulkTriageResponse {
  results: BulkTriageResult[]
}

const RETAILER_FIELD_NAME = "retailer_name"

function showUpdateRetailer(orgFieldConfigs: OrgFieldConfig[] | undefined, isAccounting: boolean): boolean {
  if (!orgFieldConfigs) return false
  const tableContext = isAccounting ? "accounting_table" : "deduction_table"
  return orgFieldConfigs.some(
    (c: OrgFieldConfig) =>
      c.field_name === RETAILER_FIELD_NAME && c.is_active && c.table_contexts.includes(tableContext)
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("showUpdateRetailer", () => {
    it("only shows bulk retailer editing when enabled for the current table", () => {
      expect(showUpdateRetailer(undefined, false)).toBe(false)
      expect(showUpdateRetailer([], true)).toBe(false)
      expect(
        showUpdateRetailer(
          [
            {
              id: "field-1",
              org_id: "org-1",
              field_name: RETAILER_FIELD_NAME,
              field_type: FieldType.Dropdown,
              display_name: "Retailer",
              display_order: 1,
              is_active: true,
              table_contexts: ["deduction_table"],
              created_at: "2026-08-13",
              updated_at: "2026-08-13",
            },
          ],
          false
        )
      ).toBe(true)
      expect(
        showUpdateRetailer(
          [
            {
              id: "field-1",
              org_id: "org-1",
              field_name: RETAILER_FIELD_NAME,
              field_type: FieldType.Dropdown,
              display_name: "Retailer",
              display_order: 1,
              is_active: true,
              table_contexts: ["deduction_table"],
              created_at: "2026-08-13",
              updated_at: "2026-08-13",
            },
          ],
          true
        )
      ).toBe(false)
    })
  })
}

export function DeductionTableSelectionBar(props: DeductionTableSelectionBarProps) {
  let { table, isAccounting, orgFieldConfigs } = props
  let [users, setUsers] = useState<User[]>([])
  let [assignmentPopoverOpen, setAssignmentPopoverOpen] = useState(false)
  let [pendingAssignValues, setPendingAssignValues] = useState<any | null>(null)
  let [assignBusy, setAssignBusy] = useState(false)
  let [bulkCategoryPopoverOpen, setBulkCategoryPopoverOpen] = useState(false)
  let [requestBackupDisabled, setRequestBackupDisabled] = useState(false)
  let [fullCodingDialogOpen, setFullCodingDialogOpen] = useState(false)
  let [bulkPromotionMatchDialogOpen, setBulkPromotionMatchDialogOpen] = useState(false)
  let [customWizOpen, setCustomWizOpen] = useState(false)
  let [customWizDeductions, setCustomWizDeductions] = useState<CustomWizDeduction[]>([])
  let [bulkTriageLoading, setBulkTriageLoading] = useState(false)
  let [bulkParseLoading, setBulkParseLoading] = useState(false)
  let [backupZipLoading, setBackupZipLoading] = useState(false)
  let [updateRetailerPopoverOpen, setUpdateRetailerPopoverOpen] = useState(false)
  let [selectedRetailer, setSelectedRetailer] = useState<string>("")

  function handleBulkCategoryPopoverInteractOutside(e: Event) {
    if (interaction_target_is_inside_radix_portal(e.target)) {
      e.preventDefault()
    }
  }

  function handleUpdateRetailerDialogDismissGuard(e: Event) {
    if (interaction_target_is_inside_radix_portal(e.target)) {
      e.preventDefault()
    }
  }

  useAsyncEffect(async () => {
    let res = await api_fetch<User[]>("/users", { use_cache: true })
    if (!res.ok) {
      console.error(`Failed to fetch users`)
      return
    }
    setUsers(res.value.data)
  }, [])

  async function handleBulkCategoryUpdate(values: z.infer<typeof CATEGORY_FORM_SCHEMA>) {
    const selectedRows = table.getSelectedRowModel().rows
    let body = selectedRows.map(row => ({
      deduction_id: row.original.id,
      ...values,
    }))

    let res = await api_fetch<BulkCategoryUpdateResponse>("/deductions/categories/bulk", {
      body,
    })
    toastResult(
      res,
      `${selectedRows.length} deduction${selectedRows.length > 1 ? "s" : ""} updated`,
      "Failed to update categories"
    )
    if (!res.ok) {
      return
    }
    setBulkCategoryPopoverOpen(false)
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  function selectedRowsWithPendingTask() {
    // The list row's `task` JSON mirrors the preferred task and is pending whenever the deduction
    // has at least one pending task.
    return table.getSelectedRowModel().rows.filter(row => row.original.task?.status === TaskStatus.Pending)
  }

  async function submitTaskAssignment(values: any, complete_existing: boolean) {
    const selectedRows = table.getSelectedRowModel().rows
    setAssignBusy(true)
    try {
      if (complete_existing) {
        const pending_ids = selectedRowsWithPendingTask().map(row => row.original.id)
        if (pending_ids.length > 0) {
          const complete_res = await bulk_complete_tasks(pending_ids)
          if (!complete_res.ok) {
            toastResult(complete_res, "", "Failed to complete existing tasks")
            return
          }
        }
      }
      // One task per selected deduction per selected assignee.
      let tasks: TaskSubmission[] = selectedRows.flatMap(row =>
        values.user_ids.map((user_id: string) => ({
          deduction_id: row.original.id,
          user_id,
          task_type: values.taskType,
          backup_type: values.backupType,
          note: values.note,
          is_time_sensitive: values.isTimeSensitive,
        }))
      )

      let res = await assign_tasks(tasks)
      toastResult(res, `${tasks.length} task${tasks.length > 1 ? "s" : ""} assigned`, "Failed to assign tasks")
      if (!res.ok) {
        return
      }
      setPendingAssignValues(null)
      setAssignmentPopoverOpen(false)
      triggerRefreshFlag()
      ;(table.options.meta as any).resetRowSelection()
    } finally {
      setAssignBusy(false)
    }
  }

  async function handleTaskAssignment(values: any) {
    if (selectedRowsWithPendingTask().length > 0) {
      setPendingAssignValues(values)
      return
    }
    await submitTaskAssignment(values, false)
  }

  function selectedCodingRows(): BulkCodingRow[] {
    return table.getSelectedRowModel().rows.map((row: { original: DeductionListJson }) => ({
      id: row.original.id,
      source: row.original.source,
      kind: row.original.kind,
      invoice_amount: row.original.invoice_amount,
      discount_amount: row.original.discount_amount,
      invoice_number: row.original.invoice_number,
    }))
  }

  async function handleSelectAccountingMatch(match: SelectItem) {
    const deductionIds = table.getSelectedRowModel().rows.map(row => row.original.id)
    if (!(await bulk_code_to_account(deductionIds, match.id))) {
      return
    }
    setAssignmentPopoverOpen(false)
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function handleBulkFullCode(formValues: Partial<Split>) {
    // Positive coding amount per transaction; early-pay invoices book only their discount
    if (!(await bulk_full_code(selectedCodingRows(), formValues))) {
      return
    }

    setFullCodingDialogOpen(false)
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function bulkRequestBackup() {
    if (requestBackupDisabled) return

    setRequestBackupDisabled(true)
    let rows = table.getSelectedRowModel().rows
    let seenIds = new Set<string>()
    let eligible = rows.filter(r => {
      if (r.original.action?.type !== "request_backup") {
        return false
      }
      if (seenIds.has(r.original.id)) {
        return false
      }
      seenIds.add(r.original.id)
      return true
    })
    let skipped = rows.length - eligible.length
    if (eligible.length === 0) {
      setRequestBackupDisabled(false)
      toast({
        title: "No eligible deductions",
        description: "Selected rows already have backup requested or are not eligible.",
        variant: "destructive",
      })
      return
    }
    let limit_error = bulk_backup_request_limit_error(eligible.length)
    if (limit_error) {
      setRequestBackupDisabled(false)
      toast({
        title: "Too many backup requests at once",
        description: limit_error,
        variant: "destructive",
      })
      return
    }
    let forms = eligible.map(r => {
      let d = r.original
      let is_rerequest = d.action?.type === "request_backup" ? d.action.is_rerequest : false
      return build_request_backup_form(d, { is_rerequest })
    })
    let res = await bulk_submit_dispute(forms)
    setRequestBackupDisabled(false)
    if (res.ok && skipped > 0) {
      toast({
        title: `Requested backup for ${eligible.length} deduction${eligible.length === 1 ? "" : "s"}`,
        description: `Skipped ${skipped} row${skipped === 1 ? "" : "s"} that already have backup requested or were duplicates.`,
      })
    } else {
      toastResult(
        res,
        `${eligible.length} backup request${eligible.length === 1 ? "" : "s"} sent`,
        "Failed to request backup",
      )
    }
    if (!res.ok) {
      return
    }
    setAssignmentPopoverOpen(false)
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function bulkUpdateStatus(status: String) {
    let rows = table.getSelectedRowModel().rows
    let body = rows.map(row => ({
      deduction_id: row.original.id,
      status,
      notes: "bulk",
    }))

    let res = await api_fetch("/statuses", { body })
    toastResult(
      res,
      `${rows.length} deduction${rows.length > 1 ? "s" : ""} marked as ${status}`,
      `Failed to mark deductions as ${status}`
    )
    if (!res.ok) {
      return
    }
    setAssignmentPopoverOpen(false)
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function handleBulkUpdateRetailer(retailerName: string) {
    const rows = table.getSelectedRowModel().rows
    const deductionIds = rows.map((row: { original: DeductionListJson }) => row.original.id)
    const res = await api_fetch("/deductions/retailer/bulk", {
      method: "POST",
      body: {
        deduction_ids: deductionIds,
        retailer_name: retailerName.trim() === "" ? null : retailerName.trim(),
      },
    })
    toastResult(
      res,
      `${rows.length} deduction${rows.length > 1 ? "s" : ""} retailer updated`,
      "Failed to update retailer"
    )
    if (!res.ok) return
    setUpdateRetailerPopoverOpen(false)
    setSelectedRetailer("")
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function handleDownloadBackupZip() {
    if (backupZipLoading) return
    const rows = table.getSelectedRowModel().rows
    const deduction_ids = rows.map((row: { original: DeductionListJson }) => row.original.id)
    setBackupZipLoading(true)
    try {
      await download_post_export("/api/deductions/backup-zip", { deduction_ids }, "Deduction_backup.zip")
    } catch (error) {
      toast({
        title: "Backup download failed",
        description: error instanceof Error ? error.message : "Request failed",
        variant: "destructive",
      })
    } finally {
      setBackupZipLoading(false)
    }
  }

  async function handleBulkParseBackup() {
    if (bulkParseLoading) return
    const rows = table.getSelectedRowModel().rows
    const deductionIds = rows.map((row: { original: DeductionListJson }) => row.original.id)
    setBulkParseLoading(true)
    const res = await bulk_parse_backup(deductionIds)
    setBulkParseLoading(false)
    if (!res.ok) {
      toast({
        title: "Parse backup failed",
        description: res.error.message ?? "Request failed",
        variant: "destructive",
      })
      return
    }
    const results = res.value.data.results
    const ok = results.filter((r) => r.success && !r.skipped).length
    const skipped = results.filter((r) => r.skipped).length
    const failed = results.filter((r) => !r.skipped && !r.success).length
    if (failed > 0 || skipped > 0) {
      toast({
        title: "Parse backup finished",
        description: `${ok} succeeded, ${failed} failed, ${skipped} skipped`,
        variant: failed > 0 ? "destructive" : "default",
      })
    } else {
      toast({
        title: "Parse backup finished",
        description: `${ok} deduction${ok !== 1 ? "s" : ""} processed`,
      })
    }
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  async function handleBulkCompleteTask() {
    const rows = table.getSelectedRowModel().rows
    const deductionIds = rows.map((row: { original: DeductionListJson }) => row.original.id)
    const res = await bulk_complete_tasks(deductionIds)
    toastResult(
      res,
      `${res.ok ? res.value.completed_count : 0} task${(res.ok ? res.value.completed_count : 0) !== 1 ? "s" : ""} completed`,
      "Failed to complete tasks"
    )
    if (!res.ok) return
    triggerRefreshFlag()
    ;(table.options.meta as any).resetRowSelection()
  }

  /**
   * Bulk CodingWiz runs in the background (`start_bulk_coding_wiz`): each row streams its own
   * progress into the table and the floating panel, so the selection is released right away
   * instead of holding the bar open until the whole batch returns.
   */
  function handleBulkCodingWiz() {
    const started = start_bulk_coding_wiz_for_rows(selectedCodingRows(), coding_wiz_prefs, isAccounting)
    if (started) {
      ;(table.options.meta as any).resetRowSelection()
    }
  }

  async function handleBigWiz() {
    const rows = table.getSelectedRowModel().rows
    const deductionIds = rows.map((row: { original: DeductionListJson }) => row.original.id)
    if (deductionIds.length === 0) return

    ;(table.options.meta as any).resetRowSelection()
    await run_big_wiz(deductionIds)
  }

  async function handleBulkTriage() {
    if (bulkTriageLoading) return

    const selectedRows = table.getSelectedRowModel().rows
    const deductionIds = selectedRows.map(row => row.original.id)

    setBulkTriageLoading(true)
    addTriageInProgress(deductionIds)

    try {
      const res = await api_fetch<BulkTriageResponse>("/deductions/triage/bulk", {
        method: "POST",
        body: { deduction_ids: deductionIds },
      })

      if (!res.ok) {
        toast({
          title: "Wizard analysis failed",
          description: "Failed to run AI analysis on selected deductions",
          variant: "destructive",
        })
        return
      }

      const results = res.value.data.results
      const successCount = results.filter((r: BulkTriageResult) => r.success).length
      const failCount = results.filter((r: BulkTriageResult) => !r.success).length

      if (failCount > 0) {
        toast({
          title: "Wizard analysis completed with errors",
          description: `${successCount} succeeded, ${failCount} failed`,
          variant: "destructive",
        })
      } else {
        toast({
          title: "Wizard analysis complete",
          description: `Successfully analyzed ${successCount} deduction${successCount !== 1 ? "s" : ""}`,
        })
      }

      triggerRefreshFlag()
      ;(table.options.meta as any).resetRowSelection()
    } catch (err) {
      console.error("Bulk triage failed:", err)
      toast({
        title: "Wizard analysis failed",
        description: "An unexpected error occurred",
        variant: "destructive",
      })
    } finally {
      removeTriageInProgress(deductionIds)
      setBulkTriageLoading(false)
    }
  }

  const user = UserState.use()
  const { prefs: coding_wiz_prefs } = use_coding_wiz_preferences()
  let statusValues: Record<string, string> = { ...STATUS_VALUES }
  if (isSuperCoffeeUser(user?.org_id)) {
    statusValues = { ...statusValues, pending: DEDUCTION_STATUS_LABELS.pending }
  }

  return (
    // `w-max` + auto margins: a fixed box positioned at `left-1/2` shrink-wraps to the space
    // right of the midline, so it wrapped at half the viewport. Sized to its content it stays
    // on one row and only wraps once the viewport is narrower than the buttons.
    <div class="flex flex-wrap items-center justify-center gap-y-2 fixed bottom-10 z-10 bg-slate-900 rounded-lg border inset-x-4 mx-auto w-max max-w-[calc(100vw-2rem)] p-2">
      <div className="flex justify-start">
        <Button
          onClick={(table.options.meta as any).resetRowSelection}
          className="text-slate-300 hover:text-slate-100"
          variant="link">
          {`${table.getFilteredSelectedRowModel().rows.length} selected`}
          <X className="h-4" />
        </Button>
      </div>
      <Separator orientation="vertical" className="h-10 bg-slate-700 mr-3" />
      <div className="flex flex-wrap justify-center gap-3">
        {!isAccounting && (
          <Button
            className="pr-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300"
            onClick={() => handleBulkTriage()}
            disabled={bulkTriageLoading}>
            {bulkTriageLoading ? (
              <>
                <span class="text-purple-300">Analyzing...</span>
                <LoaderCircle className="h-4 text-purple-300 animate-spin" />
              </>
            ) : (
              <>
                <span class="text-purple-300">Ask Wizard</span>
                <Wand2 className="h-4 text-purple-300" />
              </>
            )}
          </Button>
        )}
        {!isAccounting &&
          table.getFilteredSelectedRowModel().rows.length >= 2 &&
          table.getFilteredSelectedRowModel().rows.length <= 5 && (
            <Button
              className="pr-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-300"
              onClick={() => openBulkWizard()}>
              <span class="text-violet-300">Bulk Wizard</span>
              <Sparkles className="h-4 text-violet-300" />
            </Button>
          )}
        {table.getFilteredSelectedRowModel().rows.every(row => row.original.kind === "deduction") && (
          <Button
            className="pr-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-300"
            onClick={() => bulkRequestBackup()}
            disabled={requestBackupDisabled}>
            <span class="text-orange-300">Request backup</span>
            <File className="h-4 text-orange-300" />
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="pr-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300">
              <span className="text-emerald-300">Update stage</span>
              <CircleCheck className="h-4 text-emerald-300" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto">
            {Object.entries(statusValues).map(([status, label]) => (
              <DropdownMenuItem key={status} onClick={() => bulkUpdateStatus(status)}>
                <div className="flex items-center">
                  <Badge className="px-1 pr-2 mr-1 rounded-sm text-center" variant={status as any}>
                    {STATUS_ICONS[status as keyof typeof STATUS_ICONS]}
                    {label}
                  </Badge>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {isAccounting && (
          <AutocompleteSelect
            onSelect={handleSelectAccountingMatch}
            values={LedgerAccounts.get()}
            trigger={<CodePill variant="dark" />}
          />
        )}
        {isAccounting && (
          <>
            <Button
              className="pr-2 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-300"
              onClick={() => setFullCodingDialogOpen(true)}>
              <span className="text-indigo-300">Fully code</span>
              <Code className="h-4 text-indigo-300" />
            </Button>
            <FullCodingDialog
              open={fullCodingDialogOpen}
              onOpenChange={setFullCodingDialogOpen}
              onSubmit={handleBulkFullCode}
              selectedTransactionIds={table.getSelectedRowModel().rows.map(row => row.original.id)}
            />
          </>
        )}
        {/* Coding's most-used action, so it sits beside the two manual coding controls rather
            than under More — it is the one a coder reaches for first on a check. */}
        {isAccounting && (
          <Button
            className="pr-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-300"
            onClick={handleBulkCodingWiz}>
            <span className="text-violet-300">CodingWiz</span>
            <Wand2 className="h-4 text-violet-300" />
          </Button>
        )}
        <Button
          className="pr-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300"
          onClick={() => setBulkPromotionMatchDialogOpen(true)}>
          <span className="text-purple-300">Match promotion</span>
          <Star className="h-4 text-purple-300" />
        </Button>
        <BulkPromotionMatchDialog
          open={bulkPromotionMatchDialogOpen}
          onOpenChange={setBulkPromotionMatchDialogOpen}
          selectedTransactionIds={table.getSelectedRowModel().rows.map(row => row.original.id)}
          itemNoun={isAccounting ? "transaction" : "deduction"}
          searchContextSeed={buildPromotionSearchSeedFromSelection(
            table.getSelectedRowModel().rows.map(row => row.original)
          )}
          onSuccess={() => {
            ;(table.options.meta as any).resetRowSelection()
          }}
        />
        <Popover open={assignmentPopoverOpen} onOpenChange={() => setAssignmentPopoverOpen(true)}>
          <PopoverTrigger asChild>
            <Button className="pr-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300">
              <span class="text-amber-300">Assign task</span>
              <CircleCheck className="h-4 text-amber-300" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[324px]">
            <AssignmentForm users={users} onSubmit={handleTaskAssignment} />
          </PopoverContent>
        </Popover>
        <PendingTaskConfirmDialog
          open={pendingAssignValues !== null}
          busy={assignBusy}
          onCompleteExisting={() => void submitTaskAssignment(pendingAssignValues, true)}
          onKeepExisting={() => void submitTaskAssignment(pendingAssignValues, false)}
          onCancel={() => setPendingAssignValues(null)}>
          <div>
            {selectedRowsWithPendingTask().length} of the selected deductions already{" "}
            {selectedRowsWithPendingTask().length === 1 ? "has" : "have"} a pending task. Mark those complete before
            assigning the new task?
          </div>
        </PendingTaskConfirmDialog>

        <Popover open={bulkCategoryPopoverOpen} onOpenChange={setBulkCategoryPopoverOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button className="pr-2 bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-300">
              <span className="text-fuchsia-300">Update category</span>
              <Tag className="h-4 text-fuchsia-300" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            sideOffset={8}
            collisionPadding={16}
            onInteractOutside={handleBulkCategoryPopoverInteractOutside}>
            <CategoryForm
              deductionId={table.getSelectedRowModel()?.rows[0]?.original?.id}
              onSubmit={handleBulkCategoryUpdate}
            />
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="pr-2 bg-slate-600/50 hover:bg-slate-600/70 border-slate-500/50 text-slate-200"
              variant="outline">
              <Menu className="h-4 text-slate-200" />
              <span class="text-slate-200">More</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40 p-1.5 flex flex-col gap-2">
            <DropdownMenuItem
              onClick={() => void handleBigWiz()}
              className="rounded-md bg-indigo-500/20 text-indigo-900 focus:bg-indigo-500/30 data-highlighted:bg-indigo-500/30 py-2 px-3">
              <span class="text-indigo-900">BigWiz</span>
              <Wand2 className="h-4 ml-2 text-indigo-900 shrink-0" aria-hidden />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                const rows = table.getSelectedRowModel().rows
                setCustomWizDeductions(
                  rows.map((row: { original: DeductionListJson }) => ({
                    id: row.original.id,
                    invoice_number: row.original.invoice_number,
                  }))
                )
                setCustomWizOpen(true)
              }}
              className="rounded-md bg-violet-500/20 text-violet-900 focus:bg-violet-500/30 data-highlighted:bg-violet-500/30 py-2 px-3">
              <span class="text-violet-900">CustomWiz</span>
              <Sparkles className="h-4 ml-2 text-violet-900 shrink-0" aria-hidden />
            </DropdownMenuItem>
            {showUpdateRetailer(orgFieldConfigs, isAccounting) && (
              <DropdownMenuItem
                onClick={() => setUpdateRetailerPopoverOpen(true)}
                className="rounded-md bg-fuchsia-500/20 text-fuchsia-900 focus:bg-fuchsia-500/30 data-highlighted:bg-fuchsia-500/30 py-2 px-3">
                <span class="text-fuchsia-900">Update retailer</span>
                <Store className="h-4 ml-2 text-fuchsia-900 shrink-0" aria-hidden />
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => void handleBulkParseBackup()}
              disabled={bulkParseLoading}
              className="rounded-md bg-blue-500/20 text-blue-900 focus:bg-blue-500/30 data-highlighted:bg-blue-500/30 py-2 px-3">
              <span class="text-blue-900">{bulkParseLoading ? "Parsing…" : "Parse backup"}</span>
              <FileSearch className="h-4 ml-2 text-blue-900 shrink-0" aria-hidden />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleDownloadBackupZip()}
              disabled={backupZipLoading}
              title="ZIP of every selected deduction's backup, with a summary sheet (age, customer, check, amount, stage)"
              className="rounded-md bg-sky-500/20 text-sky-900 focus:bg-sky-500/30 data-highlighted:bg-sky-500/30 py-2 px-3">
              <span class="text-sky-900">{backupZipLoading ? "Preparing ZIP…" : "Download backup"}</span>
              <Download className="h-4 ml-2 text-sky-900 shrink-0" aria-hidden />
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleBulkCompleteTask()}
              className="rounded-md bg-emerald-500/20 text-emerald-900 focus:bg-emerald-500/30 data-highlighted:bg-emerald-500/30 py-2 px-3">
              <span class="text-emerald-900">Complete task</span>
              <CircleCheck className="h-4 ml-2 text-emerald-900 shrink-0" aria-hidden />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <CustomWizDialog
          open={customWizOpen}
          onOpenChange={setCustomWizOpen}
          deductions={customWizDeductions}
          onComplete={() => {
            triggerRefreshFlag()
            ;(table.options.meta as any).resetRowSelection()
          }}
        />

        {showUpdateRetailer(orgFieldConfigs, isAccounting) && (
          <Dialog open={updateRetailerPopoverOpen} onOpenChange={setUpdateRetailerPopoverOpen}>
            <DialogContent
              className="w-[280px]"
              onInteractOutside={handleUpdateRetailerDialogDismissGuard}
              onPointerDownOutside={handleUpdateRetailerDialogDismissGuard}>
              <DialogHeader>
                <DialogTitle>Update retailer</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                <Label>Retailer</Label>
                <OrgDropdownCell
                  fieldName={RETAILER_FIELD_NAME}
                  value={selectedRetailer}
                  orgId={table.getSelectedRowModel().rows[0]?.original?.org_id ?? ""}
                  onUpdate={setSelectedRetailer}
                  placeholder="Select retailer..."
                  className="w-full"
                  insideModal={true}
                />
                <Button className="w-full" onClick={() => handleBulkUpdateRetailer(selectedRetailer)}>
                  Update
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  )
}
